import { DEFAULT_LOCALE, type Locale } from "@i18n/react";
import type { ComponentProps, JSX } from "react";

import type { Cookies } from "./types/cookies.types";

let initialized = false;
const initPromises: Promise<void>[] = [];

export let getCookies: () => Promise<Cookies>;
if (process.env.NEXT_PUBLIC_FRAMEWORK === "next")
  getCookies = require("next/headers").cookies;
/** TODO */ else
  getCookies = async () => ({
    get: () => ({ name: "", value: "" }),
    set: () => {},
    delete: () => {},
  });

export let getLocale: (request?: Request) => Locale | Promise<Locale>;
if (process.env.NEXT_PUBLIC_FRAMEWORK === "next")
  getLocale = require("@i18n/next/server").getLocale;
else if (process.env.VITE_FRAMEWORK === "rr") {
  const p = import("@i18n/rr").then(({ getLocale: getLocaleRr }) => {
    getLocale = getLocaleRr;
  });
  initPromises.push(p);
} else getLocale = () => DEFAULT_LOCALE;

export type ImageProps = Omit<ComponentProps<"img">, "src"> & {
  fill?: boolean;
  objectFit?: "cover";
  placeholder?: "blur";
  quality?: number;
  src:
    | string
    | {
        src: string;
        height: number;
        width: number;
        blurDataURL?: string;
        blurWidth?: number;
        blurHeight?: number;
      };
};
export let Image: (props: ImageProps) => JSX.Element;
if (process.env.NEXT_PUBLIC_FRAMEWORK === "next")
  Image = require("next/image").default;
else
  Image = ({ fill, objectFit, placeholder, src, ...props }: ImageProps) => (
    <img src={typeof src === "string" ? src : src.src} {...props} />
  );

export type LinkProps = ComponentProps<"a"> & { prefetch?: boolean };
export let Link: (props: LinkProps) => JSX.Element;
if (process.env.NEXT_PUBLIC_FRAMEWORK === "next")
  Link = require("@i18n/next").Link;
else if (process.env.VITE_FRAMEWORK === "rr") {
  const p = import("@i18n/rr").then(({ Link: LinkRr }) => {
    Link = ({ prefetch, ...props }: LinkProps) => (
      <LinkRr {...(props as ComponentProps<typeof LinkRr>)} />
    );
  });
  initPromises.push(p);
} else
  Link = ({ children, ...props }: LinkProps) => <a {...props}>{children}</a>;

type ScriptProps = {
  src: string;
  strategy?: "lazyOnload";
  onLoad?: () => void;
};
export let Script: (props: ScriptProps) => JSX.Element | null;
if (process.env.NEXT_PUBLIC_FRAMEWORK === "next")
  Script = require("next/script").default;
else Script = () => null;

/**
 * Ensures all framework-specific modules are loaded before use.
 * For react-router apps, call this before rendering to avoid timing issues.
 */
export async function ensureFrameworkReady(): Promise<void> {
  if (initialized) return;
  if (initPromises.length > 0) {
    await Promise.all(initPromises);
  }
  initialized = true;
}
